import React, { useEffect, useState } from 'react';
import styled from '../../~reusables/contexts/ThemeContext';

interface LoadingBarProps {
  isLoading: boolean;
}

/**
 * The bar is animated by a CSS transition, so React only re-renders when
 * loading starts or ends. Re-rendering on every animation tick interrupts
 * React's interruptible renders, which kept large pages from ever finishing.
 */
export const LoadingBar: React.FC<LoadingBarProps> = ({ isLoading }) => {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let frameId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (isLoading) {
      setVisible(true);
      setProgress(0);

      // Wait for the empty bar to be painted so the width transition runs
      frameId = requestAnimationFrame(() => {
        frameId = requestAnimationFrame(() => setProgress(90));
      });
    } else if (visible) {
      // Complete the animation
      setProgress(100);

      // Hide after animation completes
      timeoutId = setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, 300);
    }

    return () => {
      if (frameId !== undefined) cancelAnimationFrame(frameId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isLoading, visible]);

  if (!visible) return null;

  return (
    <BarContainer>
      <Bar style={{ width: `${progress}%` }} $progress={progress} />
    </BarContainer>
  );
};

const BarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  z-index: 9999;
  background: transparent;
`;

// Moving towards 90% starts fast and slows down as it gets close
const transitions: Record<string, string> = {
  0: 'none',
  90: 'width 10s cubic-bezier(0.05, 0.7, 0.1, 1)',
  100: 'width 0.2s ease-out',
};

const Bar = styled.div<{ $progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, #3946f1, #009eff);
  transition: ${(p) => transitions[p.$progress] ?? 'none'};
  box-shadow: 0 0 8px rgba(57, 70, 241, 0.5);
`;

export default LoadingBar;
